// src/pages/GameLoginPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/app-theme.css';
import GameButton from '../components/GameButton';
import { generateSecureToken } from '../utils/generateToken';

const MAX_ATTEMPTS = 3;
const LOCKOUT_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const GameLogin: React.FC = () => {
  const navigate = useNavigate();

  const [userNameOrEmail, setUserNameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lockedOut, setLockedOut] = useState(false);

  

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => {
      setError(null);
    }, 4000);
  };

  useEffect(() => {
    const lockoutTime = localStorage.getItem('loginLockoutUntil');
    if (lockoutTime && new Date().getTime() < parseInt(lockoutTime)) {
      setLockedOut(true);
    } else {
      localStorage.removeItem('loginLockoutUntil');
      setLockedOut(false);
    }
  }, []);

  const handleFailedAttempt = () => {
    const attempts = parseInt(localStorage.getItem('loginAttempts') || '0') + 1;
    localStorage.setItem('loginAttempts', attempts.toString());

    if (attempts >= MAX_ATTEMPTS) {
      const lockoutUntil = new Date().getTime() + LOCKOUT_DURATION_MS;
      localStorage.setItem('loginLockoutUntil', lockoutUntil.toString());
      localStorage.removeItem('loginAttempts');
      navigate('/login-error');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (lockedOut) {
      navigate('/login-error');
      return;
    }

    const apiUrl = process.env.REACT_APP_GAME_API_URL;
    const apiKey = process.env.REACT_APP_GAME_API_KEY;

    if (!apiUrl || !apiKey) {
      setTimeout(() => {
        showError('Configuration error.');
      }, 0);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${apiUrl}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        },
        body: JSON.stringify({
          userName: userNameOrEmail,
          password: password
        })
      });

      if (!response.ok) {
        const data = await response.json();
        handleFailedAttempt();
        throw new Error(data.detail || 'Login failed');
      }

      const data = await response.json();
      // TODO: Store tokens securely or route to the main game page
      sessionStorage.setItem('token', data.access_token);
      sessionStorage.setItem('userId', data.userId);
      sessionStorage.setItem('emailAddress', data.emailAddress);

      const sanityToken = await generateSecureToken();
      sessionStorage.setItem('sanityToken', sanityToken);
      
      // Send it to the backend:
      await fetch(`${apiUrl}/users/sanity-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey,
          Authorization: `Bearer ${data.access_token}`
        },
        body: JSON.stringify({
          userId: data.userId,
          sanityToken : sanityToken
        })
      });

      navigate('/launchgame');
    } catch (err: any) {
        setTimeout(() => {
            showError(err.message);
        }, 0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '60vh',
        maxWidth: '400px',
        margin: '0 auto',
        padding: '2rem',
        color: 'var(--text-normal)'
      }}
    >
      <h1 style={{ color: 'var(--text-header)', textAlign: 'center' }}>Re-Word Game Login</h1>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Username or Email</label>
          <input
            type="text"
            value={userNameOrEmail}
            onChange={(e) => setUserNameOrEmail(e.target.value)}
            className="input-field"
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-field"
          />
        </div>

        <div style={{ height: '1.5rem', textAlign: 'center', color: 'red' }}>
          {error && <span>{error}</span>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
          <GameButton
            type="submit"
            label={loading ? 'Logging in...' : 'Login'}
            disabled={loading || !userNameOrEmail || !password}
          />
        </div>
      </form>
    </div>
  );
};

export default GameLogin;
